"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.FreeShippingPromotion = void 0;
const shipping_rule_1 = require("../../shipping-rule");
const typings_1 = require("../../typings");
const utils_1 = require("../../utils");
const base_1 = require("../base");
class FreeShippingPromotion extends base_1.BasePromotion {
    constructor(props) {
        super(props);
        this.props = props;
    }
    /**
     * Get award from total price (including additional discount) or award from sum of product skus flag
     * @returns {boolean}
     */
    get isAwardFromTotalPrice() {
        return this.props.settings.awardTotalPrice;
    }
    /**
     * Calculate shipping discount from shipping rules
     *
     * Check whether every remaining product sku has shipping rule or not
     * otherwise, apply default shipping price to remaining shipping price
     * right away
     * @param newCart
     * @param remainingProductSKUs
     * @returns {Cart}
     */
    calculateShippingDiscountFromShippingRules(newCart, remainingProductSKUs) {
        var _a, _b, _c, _d;
        // total discount and remaining shipping price for particular promotion
        let totalDiscount = 0;
        let remainingShippingPrice = 0;
        // Check for "selectedShipping" first as "shipping" is deprecated
        if (!newCart.selectedShipping && !newCart.shipping) {
            return newCart;
        }
        // Get backward compatible shipping rules from remaining product skus
        const shippingRulesOfRemainingProductSKUs = (0, utils_1.getShippingRulesOfProductSKUs)(newCart, remainingProductSKUs);
        /**
         * Remaining product skus do not have any shipping rule, so the promotion will fall
         * back to chosen shipping method and apply that shipping method rate as discount
         */
        if (!shippingRulesOfRemainingProductSKUs.length) {
            // Get shipping price from selected shipping first before falling back to deprecated price
            const shippingPrice = ((_a = newCart.selectedShipping) === null || _a === void 0 ? void 0 : _a.price) || ((_b = newCart.shipping) === null || _b === void 0 ? void 0 : _b.rate) || 0;
            /**
             * Checking this condition to make sure that shipping price is not the same as shipping rate
             * which could cause the system to declare incorrect total discount
             * e.g.
             * shipping price: 200
             * shipping rate: 200
             * total discount: 200 - 200 = 0
             */
            totalDiscount =
                newCart.shippingPrice > shippingPrice ? newCart.shippingPrice - shippingPrice : newCart.shippingPrice;
            // Add applied promotion to only selected products
            const productSKUsWithAppliedPromotion = this.addAppliedPromotionToProductSKUs(newCart.productSKUs, {
                totalDiscount,
            });
            return this.composeNewCartWithAppliedPromotion(Object.assign(Object.assign({}, newCart), { productSKUs: productSKUsWithAppliedPromotion }), { totalDiscount });
        }
        // Check whether every remaining product skus has shipping rule or not
        const everyRemainingProductSKUHasShippingRule = (0, utils_1.isEveryProductSKUHasShippingRules)(newCart, remainingProductSKUs, shippingRulesOfRemainingProductSKUs);
        /**
         * Not every remaining product skus has shipping rule, the system will then apply
         * default shipping price to remaining shipping price
         */
        if (!everyRemainingProductSKUHasShippingRule) {
            // Get shipping price from selected shipping first before falling back to deprecated price
            const shippingPrice = ((_c = newCart.selectedShipping) === null || _c === void 0 ? void 0 : _c.price) || ((_d = newCart.shipping) === null || _d === void 0 ? void 0 : _d.rate) || 0;
            remainingShippingPrice += shippingPrice;
        }
        const shippingRules = (0, shipping_rule_1.getShippingRules)(shippingRulesOfRemainingProductSKUs);
        shippingRules.forEach((shippingRule) => {
            remainingShippingPrice += shippingRule.getShippingPrice(remainingProductSKUs);
        });
        totalDiscount = newCart.shippingPrice - remainingShippingPrice;
        // Add applied promotion to only selected products
        const productSKUsWithAppliedPromotion = this.addAppliedPromotionToProductSKUs(newCart.productSKUs, {
            totalDiscount,
        });
        return this.composeNewCartWithAppliedPromotion(Object.assign(Object.assign({}, newCart), { productSKUs: productSKUsWithAppliedPromotion }), { totalDiscount });
    }
    /**
     * Apply free shipping for any product
     * e.g. Buy any product to get free-shipping
     * @param cart
     * @returns {Cart}
     */
    applyFreeShippingForAnyProductPromotion(cart) {
        if (!cart.productSKUs.length || !this.isMatchedSelectedShippingMethod(cart)) {
            return cart;
        }
        const newCart = Object.assign({}, cart);
        // Add applied promotion to every product skus
        const productSKUsWithAppliedPromotion = this.addAppliedPromotionToProductSKUs(newCart.productSKUs, {
            totalDiscount: newCart.shippingPrice,
        });
        return this.composeNewCartWithAppliedPromotion(Object.assign(Object.assign({}, newCart), { productSKUs: productSKUsWithAppliedPromotion }), { totalDiscount: newCart.shippingPrice });
    }
    /**
     * Apply free shipping for any selected product
     * e.g. Buy any selected products (A, B, C) for **free-shipping for that specific product**
     * @param cart
     * @returns {Cart}
     */
    applyFreeShippingForSelectedProductPromotion(cart) {
        if (!cart.productSKUs.length || !this.isMatchedSelectedShippingMethod(cart)) {
            return cart;
        }
        const hasSelectedProducts = (0, utils_1.hasSomeSelectedProductsInProductSKUs)(cart.productSKUs, this.selectedProductIds);
        if (!hasSelectedProducts) {
            return cart;
        }
        const newCart = Object.assign({}, cart);
        // Get remaining product skus from cart by excluding selected products from promotion
        const remainingProductSKUs = (0, utils_1.getRemainingProductSKU)(newCart.productSKUs, this.selectedProductIds);
        if (!remainingProductSKUs.length) {
            // Add applied promotion to every product skus because every product is actually selected products
            const productSKUsWithAppliedPromotion = this.addAppliedPromotionToProductSKUs(newCart.productSKUs, { totalDiscount: newCart.shippingPrice });
            return this.composeNewCartWithAppliedPromotion(Object.assign(Object.assign({}, newCart), { productSKUs: productSKUsWithAppliedPromotion }), { totalDiscount: newCart.shippingPrice });
        }
        // Calculate shipping discount from remaining product skus
        return this.calculateShippingDiscountFromShippingRules(cart, remainingProductSKUs);
    }
    /**
     * Apply free shipping for any product in total of X price
     * e.g. 200B of any product to get free-shipping
     * @param cart
     * @returns {Cart}
     */
    applyFreeShippingForInTotalOfAnyProductPromotion(cart) {
        if (!cart.productSKUs.length || !this.isMatchedSelectedShippingMethod(cart)) {
            return cart;
        }
        /**
         * Award with total price which include additional discount to compare
         * against with price threshold
         * e.g.
         * 290 with threshold of 500B
         *
         * 500 (threshold) > 290 (total with additional discount)
         */
        const discount = this.isAwardFromTotalPrice
            ? this.getDiscountByTarget(cart.totalPrice)
            : this.getDiscountByTarget((0, utils_1.getTotalPrice)(cart.productSKUs));
        if (discount === undefined) {
            return cart;
        }
        const newCart = Object.assign({}, cart);
        // Add applied promotion to every product skus
        const productSKUsWithAppliedPromotion = this.addAppliedPromotionToProductSKUs(newCart.productSKUs, { totalDiscount: newCart.shippingPrice });
        return this.composeNewCartWithAppliedPromotion(Object.assign(Object.assign({}, newCart), { productSKUs: productSKUsWithAppliedPromotion }), { totalDiscount: newCart.shippingPrice });
    }
    /**
     * Apply free shipping for selected product
     * e.g. X piece of selected product for **free shipping for that specific product**
     * @param cart
     * @returns {Cart}
     */
    applyFreeShippingForXPieceOfSelectedProductPromotion(cart) {
        if (!cart.productSKUs.length || !this.isMatchedSelectedShippingMethod(cart)) {
            return cart;
        }
        const hasSelectedProducts = (0, utils_1.hasSomeSelectedProductsInProductSKUs)(cart.productSKUs, this.selectedProductIds);
        if (!hasSelectedProducts) {
            return cart;
        }
        const qualifiedSelectedProductIds = cart.productSKUs
            .map((productSKU) => {
            if (!this.selectedProductIds.includes(productSKU.id)) {
                return undefined;
            }
            const discount = this.getDiscountByTarget(productSKU.amount);
            if (discount === undefined) {
                return undefined;
            }
            return productSKU.id;
        })
            .filter((id) => !!id); // Writing it like this to workaround TS issue not recognizing Array.filter
        if (!qualifiedSelectedProductIds.length) {
            return cart;
        }
        const newCart = Object.assign({}, cart);
        const remainingProductSKUs = (0, utils_1.getRemainingProductSKU)(newCart.productSKUs, qualifiedSelectedProductIds);
        if (!remainingProductSKUs.length) {
            // Add applied promotion to every product skus because every product is actually selected products
            const productSKUsWithAppliedPromotion = this.addAppliedPromotionToProductSKUs(newCart.productSKUs, { totalDiscount: newCart.shippingPrice });
            return this.composeNewCartWithAppliedPromotion(Object.assign(Object.assign({}, newCart), { productSKUs: productSKUsWithAppliedPromotion }), { totalDiscount: newCart.shippingPrice });
        }
        return this.calculateShippingDiscountFromShippingRules(newCart, remainingProductSKUs);
    }
    /**
     * Apply free shipping for selected product in bundle
     * e.g. 3 piece of mix selected product for free shipping
     * @param cart
     * @returns {Cart}
     */
    applyFreeShippingForSelectedProductInBundlePromotion(cart) {
        const isBundled = this.bundleSetting;
        if (!isBundled) {
            throw new Error('Could not apply free shipping promotion for X piece of selected product in bundle that is not mark as bundled');
        }
        if (!cart.productSKUs.length || !this.isMatchedSelectedShippingMethod(cart)) {
            return cart;
        }
        // Total amount of products in shopping cart that are selected products
        const discount = this.getDiscountByTarget((0, utils_1.getTotalAmountOfSelectedProducts)(cart.productSKUs, this.selectedProductIds));
        if (discount === undefined) {
            return cart;
        }
        const newCart = Object.assign({}, cart);
        let productSKUsWithAppliedPromotion = [...newCart.productSKUs];
        let totalShippingDiscount = 0;
        // total discount price for particular promotion
        totalShippingDiscount = cart.shippingPrice;
        // Add applied promotion to every selected product skus
        productSKUsWithAppliedPromotion = this.addAppliedPromotionToProductSKUs(newCart.productSKUs, {
            totalDiscount: totalShippingDiscount,
        });
        return this.composeNewCartWithAppliedPromotion(Object.assign(Object.assign({}, newCart), { productSKUs: productSKUsWithAppliedPromotion }), { totalDiscount: totalShippingDiscount });
    }
    /**
     * Apply free shipping for selected product in total of X price
     * e.g. 200B of mix selected product to get free-shipping
     * @param cart
     * @returns {Cart}
     */
    applyFreeShippingForInTotalOfSelectedProductInBundlePromotion(cart) {
        if (!cart.productSKUs.length || !this.isMatchedSelectedShippingMethod(cart)) {
            return cart;
        }
        const hasSelectedProducts = (0, utils_1.hasSomeSelectedProductsInProductSKUs)(cart.productSKUs, this.selectedProductIds);
        if (!hasSelectedProducts) {
            return cart;
        }
        const isBundled = this.bundleSetting;
        if (!isBundled) {
            throw new Error('Could not apply free shipping promotion for total X price of selected product in bundle that is not mark as bundled');
        }
        /**
         * Award with total price which include additional discount to compare
         * against with price threshold
         * e.g.
         * 290 with threshold of 500B
         *
         * 500 (threshold) > 290 (total with additional discount)
         */
        const discount = this.isAwardFromTotalPrice
            ? this.getDiscountByTarget((0, utils_1.getTotalPriceIncludingDiscountOfSelectedProducts)(cart.productSKUs, this.selectedProductIds))
            : this.getDiscountByTarget((0, utils_1.getTotalPriceOfSelectedProducts)(cart.productSKUs, this.selectedProductIds));
        if (discount === undefined) {
            return cart;
        }
        const newCart = Object.assign({}, cart);
        // Get remaining product skus from cart by excluding selected products from promotion
        const remainingProductSKUs = (0, utils_1.getRemainingProductSKU)(newCart.productSKUs, this.selectedProductIds);
        if (!remainingProductSKUs.length) {
            // Add applied promotion to every product skus because every product is actually selected products
            const productSKUsWithAppliedPromotion = this.addAppliedPromotionToProductSKUs(newCart.productSKUs, { totalDiscount: newCart.shippingPrice });
            return this.composeNewCartWithAppliedPromotion(Object.assign(Object.assign({}, newCart), { productSKUs: productSKUsWithAppliedPromotion }), { totalDiscount: newCart.shippingPrice });
        }
        // Calculate shipping discount from remaining product skus
        return this.calculateShippingDiscountFromShippingRules(cart, remainingProductSKUs);
    }
    /**
     * Apply promotion
     * @param cart
     * @returns {Cart}
     */
    applyPromotion(cart) {
        switch (this.settingCondition) {
            case typings_1.PromotionCondition.BUY_ANY_PRODUCT:
                return this.applyFreeShippingForAnyProductPromotion(cart);
            case typings_1.PromotionCondition.BUY_ANY_SELECTED_PRODUCT:
                return this.applyFreeShippingForSelectedProductPromotion(cart);
            case typings_1.PromotionCondition.BUY_X_PIECE_OF_SELECTED_PRODUCT_IN_BUNDLE:
                return this.applyFreeShippingForSelectedProductInBundlePromotion(cart);
            case typings_1.PromotionCondition.BUY_X_PIECE_OF_SELECTED_PRODUCT:
                return this.applyFreeShippingForXPieceOfSelectedProductPromotion(cart);
            case typings_1.PromotionCondition.BUY_X_PRICE_IN_TOTAL_OF_ANY_PRODUCT:
                return this.applyFreeShippingForInTotalOfAnyProductPromotion(cart);
            case typings_1.PromotionCondition.BUY_X_PRICE_IN_TOTAL_OF_SELECTED_PRODUCT_IN_BUNDLE:
                return this.applyFreeShippingForInTotalOfSelectedProductInBundlePromotion(cart);
            default:
                return cart;
        }
    }
}
exports.FreeShippingPromotion = FreeShippingPromotion;
//# sourceMappingURL=free-shipping.js.map